<template>
  <div class="iq-card iq-card-sm">
    <div class="card-body">
      <!-- Venta guardada OFFLINE (Fase 2): aun no registrada ni facturada. -->
      <template v-if="isOfflineQueued">
        <div class="pos-cash-state pos-cash-state-closed mb-3">
          <i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>
          <div>
            <strong>{{ $t('pos.lbl_offline_sale_title') }}</strong>
            <p class="mb-0">{{ $t('pos.lbl_offline_sale_help') }}</p>
          </div>
        </div>
        <dl class="pos-totals mb-3">
          <dt class="pos-totals-total">{{ $t('pos.lbl_amount_due') }}</dt>
          <dd class="pos-totals-total">{{ money(sale.total) }}</dd>
        </dl>
        <button type="button" class="btn btn-primary w-100 pos-tap" @click="emit('new-sale')">
          <i class="fa-solid fa-plus me-2" aria-hidden="true"></i>{{ $t('pos.lbl_new_sale') }}
        </button>
      </template>

      <template v-else>
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h5 class="mb-0">
          {{ $t('pos.lbl_sale_registered') }}
          <small class="text-muted" v-if="booking?.id">· {{ $t('pos.lbl_sale_number') }} #{{ booking.id }}</small>
        </h5>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary pos-tap" @click="printReceipt(sale)">
            <i class="fa-solid fa-print me-2" aria-hidden="true"></i>{{ $t('pos.lbl_print_receipt') }}
          </button>
          <button type="button" class="btn btn-primary pos-tap" @click="emit('new-sale')">
            <i class="fa-solid fa-plus me-2" aria-hidden="true"></i>{{ $t('pos.lbl_new_sale') }}
          </button>
        </div>
      </div>

      <!--
        ESTADO DE PAGO: se pinta EXACTAMENTE lo que responde `pos/sale`
        (`payment.status`, derivado en servidor). Nunca se asume "pagado" por
        haber pulsado Cobrar.
      -->
      <div class="d-flex align-items-center gap-2 mb-3">
        <span class="badge fs-6" :class="isPaid ? 'bg-success' : 'bg-danger'">
          {{ isPaid ? $t('pos.lbl_status_paid') : $t('pos.lbl_status_unpaid') }}
        </span>
        <span class="text-muted">{{ $t('pos.lbl_payment_status') }}: {{ methodLabel }}</span>
        <span class="badge bg-info" v-if="payment?.gateway_verified">
          <i class="fa-solid fa-shield-halved me-1" aria-hidden="true"></i>{{ $t('pos.lbl_gateway_verified') }}
        </span>
      </div>

      <dl class="pos-totals">
        <dt>{{ $t('pos.lbl_subtotal') }}</dt>
        <dd>{{ money(totals?.subtotal) }}</dd>

        <template v-if="Number(totals?.coupon_discount) > 0">
          <dt class="text-success">{{ $t('pos.lbl_coupon_discount') }}</dt>
          <dd class="text-success">- {{ money(totals.coupon_discount) }}</dd>
        </template>

        <dt>{{ $t('pos.lbl_taxable_base') }}</dt>
        <dd>{{ money(totals?.taxable_base) }}</dd>
        <dt>{{ $t('pos.lbl_tax') }}</dt>
        <dd>{{ money(totals?.tax) }}</dd>

        <template v-if="Number(totals?.tip) > 0">
          <dt>{{ $t('pos.lbl_tip') }}</dt>
          <dd>{{ money(totals.tip) }}</dd>
        </template>

        <dt class="pos-totals-strong">{{ $t('pos.lbl_total') }}</dt>
        <dd class="pos-totals-strong">{{ money(totals?.total) }}</dd>

        <!-- Tenders reales aplicados por el servidor (no bajan el IVA). -->
        <template v-if="Number(totals?.points_used) > 0">
          <dt>{{ $t('pos.lbl_points_used') }}</dt>
          <dd>{{ totals.points_used }} ({{ money(totals.points_value) }})</dd>
        </template>
        <template v-if="Number(totals?.gift_card_applied) > 0">
          <dt>{{ $t('pos.lbl_gift_card_applied') }}</dt>
          <dd>{{ money(totals.gift_card_applied) }}</dd>
        </template>
        <template v-if="Number(totals?.wallet_used) > 0">
          <dt>{{ $t('pos.lbl_wallet_used') }}</dt>
          <dd>{{ money(totals.wallet_used) }}</dd>
        </template>

        <dt class="pos-totals-total">{{ $t('pos.lbl_amount_due') }}</dt>
        <dd class="pos-totals-total">{{ money(totals?.amount_due) }}</dd>
      </dl>

      <p class="text-muted small mb-0" v-if="sale?.cash_register_id">
        <i class="fa-solid fa-cash-register me-1" aria-hidden="true"></i>
        {{ $t('pos.lbl_cash_movement') }}: #{{ sale.cash_register_id }}
      </p>

      <p class="text-success small mb-0" v-if="sale?.delivery && sale.delivery.created">
        <i class="fa-solid fa-truck me-1" aria-hidden="true"></i>
        {{ $t('pos.lbl_delivery_created') }}: {{ $t('pos.lbl_delivery_order') }} #{{ sale.delivery.order_id }}
      </p>

      <hr />

      <!-- ================= FACTURA SRI ================= -->
      <h6 class="mb-2">{{ $t('pos.lbl_invoice_status') }}</h6>

      <p class="text-muted mb-0" v-if="!invoice">{{ $t('pos.lbl_invoice_not_requested') }}</p>

      <template v-else>
        <div class="d-flex align-items-center gap-2 mb-2">
          <span class="badge fs-6" :class="invoiceBadge.variant">{{ invoiceBadge.label }}</span>
          <span class="text-muted small" v-if="invoice.estado">({{ invoice.estado }})</span>
        </div>

        <!-- Mensaje de error de emision tal cual lo devuelve el backend. -->
        <AlertMessage
          class="mb-2"
          variant="danger"
          v-if="invoice.emitted === false"
          :message="invoice.message || $t('pos.lbl_unexpected_error')"
          :title="$t('pos.lbl_error')"
        />
        <AlertMessage class="mb-2" variant="warning" v-if="invoice.emitted === false" :message="$t('pos.lbl_invoice_error_help')" />
        <AlertMessage class="mb-2" variant="info" v-else-if="invoiceState === 'pending'" :message="$t('pos.lbl_invoice_pending_help')" />

        <dl class="pos-totals" v-if="invoice.numero_comprobante || invoice.clave_acceso || invoice.numero_autorizacion">
          <template v-if="invoice.numero_comprobante">
            <dt>{{ $t('pos.lbl_invoice_number') }}</dt>
            <dd class="pos-totals-strong">{{ invoice.numero_comprobante }}</dd>
          </template>
          <template v-if="invoice.clave_acceso">
            <dt>{{ $t('pos.lbl_access_key') }}</dt>
            <dd class="text-break">{{ invoice.clave_acceso }}</dd>
          </template>
          <template v-if="invoice.numero_autorizacion">
            <dt>{{ $t('pos.lbl_authorization') }}</dt>
            <dd class="text-break">{{ invoice.numero_autorizacion }}</dd>
          </template>
        </dl>

        <a
          v-if="rideUrl"
          class="btn btn-outline-primary btn-sm pos-tap"
          :href="rideUrl"
          target="_blank"
          rel="noopener"
        >
          <i class="fa-solid fa-file-pdf me-2" aria-hidden="true"></i>{{ $t('pos.lbl_view_ride') }}
        </a>
      </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import { money } from '../money'
import { printReceipt } from '../receipt'
import { POS_RIDE_URL, PAYMENT_METHODS } from '../constants/pos'

const props = defineProps({
  sale: { type: Object, default: null }
})

const emit = defineEmits(['new-sale'])
const { t } = useI18n()

const isOfflineQueued = computed(() => props.sale?.offline_queued === true)
const booking = computed(() => props.sale?.booking || null)
const totals = computed(() => props.sale?.totals || null)
const payment = computed(() => props.sale?.payment || null)
const invoice = computed(() => props.sale?.invoice || null)

/** Unica fuente de verdad del cobro: el estado derivado por el servidor. */
const isPaid = computed(() => payment.value?.status === 'paid')

/**
 * El backend responde el metodo ya normalizado (minusculas). Se traduce si se
 * conoce; si no, se muestra crudo antes que inventarse una etiqueta.
 */
const methodLabel = computed(() => {
  const found = PAYMENT_METHODS.find((item) => item.value === payment.value?.method)
  return found ? t(found.label) : payment.value?.method || ''
})

/**
 * Estados reales de `sri_documents.estado` (ComprobanteService):
 *   creado | firmado | enviado -> en curso, el SRI aun no autoriza
 *   autorizado                 -> OK
 *   no_autorizado | devuelto   -> rechazado
 * `emitted:false` = la emision fallo antes de crear el documento.
 */
const invoiceState = computed(() => {
  if (!invoice.value) return 'none'
  if (invoice.value.emitted === false) return 'error'

  switch (invoice.value.estado) {
    case 'autorizado':
      return 'authorized'
    case 'no_autorizado':
    case 'devuelto':
      return 'rejected'
    default:
      return 'pending'
  }
})

const invoiceBadge = computed(() => {
  switch (invoiceState.value) {
    case 'authorized':
      return { variant: 'bg-success', label: t('pos.lbl_invoice_authorized') }
    case 'rejected':
      return { variant: 'bg-danger', label: t('pos.lbl_invoice_rejected') }
    case 'error':
      return { variant: 'bg-danger', label: t('pos.lbl_invoice_error') }
    default:
      return { variant: 'bg-warning text-dark', label: t('pos.lbl_invoice_pending') }
  }
})

// El RIDE solo existe si se llego a crear el documento SRI.
const rideUrl = computed(() => (invoice.value?.sri_document_id ? POS_RIDE_URL(invoice.value.sri_document_id) : null))
</script>
